import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";
import { absolutize, BAND_PLACEHOLDER } from "../../utils/media.js";
import { api } from "../../services/api.js";

const MAX_MB = 2;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const MIN_DIM = 1080;       // suositus, ei kovapakkoa

// Fallback-SVG (jos placeholder puuttuu)
const FALLBACK_DATA =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256">
       <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
         <stop offset="0" stop-color="#f9fafb"/><stop offset="1" stop-color="#eef2ff"/>
       </linearGradient></defs>
       <rect width="100%" height="100%" fill="url(#g)"/>
       <g fill="#94a3b8">
         <circle cx="128" cy="108" r="34"/>
         <rect x="48" y="156" width="160" height="60" rx="12"/>
       </g>
     </svg>`
  );

// --- Kuvan luku → ImageBitmap / HTMLImageElement (EXIF-orientaatio ok useimmissa selaimissa) ---
async function readImageBitmap(file) {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      /* fallback alla */
    }
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(new Error("Tiedoston luku epäonnistui."));
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("Kuvan lataus epäonnistui."));
    im.src = dataUrl;
  });
  // Matkitaan ImageBitmap-rajapintaa tarvitsemiimme kenttiin
  return { width: img.width, height: img.height, _html: img };
}

function squareCropBox(w, h) {
  if (w === h) return { sx: 0, sy: 0, sw: w, sh: h };
  if (w > h) {
    const sx = Math.floor((w - h) / 2);
    return { sx, sy: 0, sw: h, sh: h };
  }
  const sy = Math.floor((h - w) / 2);
  return { sx: 0, sy, sw: w, sh: w };
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob() antoi null"))), type, quality);
  });
}

// Pakkaa toistuvasti alle MAX_BYTES jos mahdollista
async function encodeImage(canvas, preferType = "image/webp") {
  let q = 0.9;
  let blob;
  try {
    blob = await canvasToBlob(canvas, preferType, q);
  } catch {
    // Selain ei tue webp → JPEG fallback
    preferType = "image/jpeg";
    q = 0.92;
    blob = await canvasToBlob(canvas, preferType, q);
  }
  while (blob.size > MAX_BYTES && q > 0.6) {
    q -= 0.1;
    blob = await canvasToBlob(canvas, preferType, q);
  }
  return blob;
}

function renameWithExt(baseName, mime) {
  const base = String(baseName || "profile-image").replace(/\.[^.]+$/, "");
  const ext = mime === "image/webp" ? "webp" : mime === "image/jpeg" ? "jpg" : "png";
  return `${base}.${ext}`;
}

// Prosessoi: keskicrop neliöksi + skaalaa tarvittaessa + WEBP (tai JPEG)
async function processToWebp(file) {
  const bmp = await readImageBitmap(file);
  const w = bmp.width, h = bmp.height;

  // Varoitus jos pieni (ei estä)
  if (Math.min(w, h) < MIN_DIM) {
    toast.warn(`Kuva on pienehkö – suositus vähintään ${MIN_DIM}×${MIN_DIM}px.`, {
      containerId: "global",
    });
  }

  const { sx, sy, sw, sh } = squareCropBox(w, h);

  // Ei skaalauspakkoa; jos haluat alasamplea isosta kuvasta, voit rajoittaa esim. 2000px:iin
  const target = Math.min(sw, sh); // neliön sivu
  const canvas = document.createElement("canvas");
  canvas.width = target;
  canvas.height = target;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Piirrä lähde canvakseen
  if ("_html" in bmp) {
    ctx.drawImage(bmp._html, sx, sy, sw, sh, 0, 0, target, target);
  } else {
    ctx.drawImage(bmp, sx, sy, sw, sh, 0, 0, target, target);
  }

  const blob = await encodeImage(canvas, "image/webp");
  return new File([blob], renameWithExt(file.name, blob.type), { type: blob.type, lastModified: Date.now() });
}

// Poimi profiilikuvan URL backendin vastauksesta (eri muotojen tuki)
const pickProfileUrl = (res) => {
  if (!res) return "";
  const band = res.band || res.data?.band || res.item || res;
  return (
    band?.profile_image_url ||
    band?.profile_image ||
    res?.profile_image_url ||
    res?.image_url ||
    res?.url ||
    ""
  );
};

export default function ProfileImageUploader({
  bandId,
  value,
  onChange,
  disabled = false,
  label = "Valitse kuva",
}) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [text, setText] = useState(value || "");
  const [broken, setBroken] = useState(false);
  const [showPath, setShowPath] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setText(value || "");
    setBroken(false);
  }, [value]);

  // Esikatselu: aina placeholder jos ei arvoa
  const previewUrl = useMemo(() => {
    const url = absolutize(text || value || "");
    const ph = BAND_PLACEHOLDER ? absolutize(BAND_PLACEHOLDER) : FALLBACK_DATA;
    return url || ph;
  }, [text, value]);

  const fileName = (text || value || "").split("/").filter(Boolean).pop() || "";
  const masked = (text || value) ? `…/${fileName}` : "";
  const hasImage = Boolean((text || value)?.trim());

  // --- upload (sis. automaattisen neliö-cropin + WEBP) ---
  const doUpload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Valitse kuvatiedosto.", { containerId: "global" });
      return;
    }

    setBusy(true);
    try {
      const processed = await processToWebp(file);

      let url = "";

     // 1) Ensisijainen: API helper (kenttä 'image' / raaka file)
      if (bandId != null) {
        try {
          const res = await api.media.setProfileFile(bandId, processed);
          url = pickProfileUrl(res);
        } catch (e) {
          const status = e?.status;
          // Jos profiili-endpoint puuttuu TAI kaatuu (500),
          // älä kaada koko uploaderia vaan anna mennä fallbackiin.
          if (![404, 405, 500].includes(status)) {
            throw e;
          }
        }
      }

      // 2) Fallback: geneerinen uploaderi
      if (!url) {
        const fd = new FormData();
        fd.append("image", processed);
        const r = await fetch(absolutize("/api/uploads/profile-image"), {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok || !data?.url) {
          throw new Error(data?.error || "Lataus epäonnistui.");
        }
        url = data.url;
      }

      onChange?.(url);
      setText(url);
      setBroken(false);
      toast.success("Kuva ladattu ja pakattu WEBP-muotoon! 📸", { containerId: "global" });
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Kuvan käsittely tai lähetys epäonnistui.", {
        containerId: "global",
      });
    } finally {
      setBusy(false);
    }
  };

  // --- poista kuva ---
  const handleRemove = async () => {
    if (disabled || busy) return;
    setBusy(true);
    try {
      setText("");
      onChange?.("");
      setBroken(false);

      if (bandId != null) {
        try {
          await api.updateBand(bandId, { profile_image: "", profile_image_url: "" });
        } catch {
          /* ei kriittinen */
        }
      }
      toast.success("Kuva poistettu. Käytetään oletuskuvaa.", { containerId: "global" });
    } finally {
      setBusy(false);
    }
  };

  // --- tapahtumakäsittelijät ---
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (file) await doUpload(file);
    e.target.value = ""; // mahdollistaa saman tiedoston uudelleenvalinnan
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || busy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) await doUpload(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (disabled || busy) return;
    setDragOver(true);
  };

  return (
    <div className="space-y-3">
      <label className="block dark:text-white/80 text-black/80 font-customFont1 text-sm mb-1">
        {label}
      </label>

      {/* Esikatselu + maskattu URL -rivi */}
      <div className="flex flex-col md:flex-row mdd:flex-row lg:flex-row xl:flex-row 2xl:flex-row items-start gap-3">
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img
          src={broken ? absolutize(BAND_PLACEHOLDER) : (previewUrl || absolutize(BAND_PLACEHOLDER))}
          onError={() => setBroken(true)}
          className="w-32 h-32 sm:w-32 sm:h-32 object-cover rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-black/5"
        />

        {/* Piilotettu arvo lomakkeelle */}
        <input type="hidden" name="profile_image" value={text || ""} />

        {/* URL + napit */}
        <div className="w-full grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto_auto_auto] items-stretch gap-2">

          <input
            readOnly
            className="w-full h-10 rounded-lg px-3 bg-white dark:bg-black/40 border border-black/10 dark:border-white/10  dark:text-white/90 text-black/90 font-customFont2 text-[12px] mb-1 font-light"
            value={showPath ? (text || "") : (hasImage ? masked : "")}
            placeholder="Profiilikuvan URL (piilotettu)"
          />

          <NeonButton
            size="sm"
            onClick={() => setShowPath((s) => !s)}
            disabled={!text}
            className="w-full scale-[0.80] md:scale-100"
          >
            {showPath ? "Piilota" : "Näytä"}
          </NeonButton>

          <NeonButton
            size="sm"
            onClick={() => navigator.clipboard.writeText(text || "")}
            disabled={!text}
            className="w-full scale-[0.80] md:scale-100"
          >
            Kopioi
          </NeonButton>

          <NeonButton
            size="sm"
            variant="secondary"
            onClick={handleRemove}
            disabled={!hasImage || disabled || busy}
            className="w-full scale-[0.80] md:scale-100"
          >
            Poista kuva
          </NeonButton>
        </div>
      </div>

      {/* Dropzone */}
      <div
        className={`rounded-xl border-2 border-dashed p-4 text-sm transition
          ${dragOver ? "border-pink/80 bg-pink/5" : "border-black/10 dark:border-white/10"}
          ${disabled || busy ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
        onClick={() => !disabled && !busy && fileInputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={() => setDragOver(false)}
        aria-label="Pudota kuva tähän tai valitse tiedosto"
      >
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div>
            <div className="dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-lg">
              Pudota kuva tähän tai valitse tiedosto
            </div>
            <div className="dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-lg opacity-70">
              Voit lisätä minkä tahansa kuvan — muunnamme sen automaattisesti neliöksi ja WEBP-muotoon.
              Suositus: <b>min {MIN_DIM}×{MIN_DIM}px</b>. Tavoitekoko: <b>≤ {MAX_MB} Mt</b>.
            </div>
          </div>
          <NeonButton size="sm" className="w-full scale-[0.80] md:scale-100">
            {busy ? "Ladataan…" : "Valitse…"}
          </NeonButton>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
          disabled={disabled || busy}
        />
      </div>
    </div>
  );
}
